import React, { useState } from 'react';
import styled from 'styled-components';
import Draggable from 'react-draggable';
import { useWindows } from '../../contexts/WindowContext';
import WindowControls from './WindowControls';

const WindowContainer = styled.div<{ 
  isMaximized: boolean; 
  isMinimized: boolean; 
  zIndex: number 
}>`
  position: fixed;
  background: rgba(20, 20, 20, 0.95);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: ${props => props.isMaximized ? '0' : '8px'};
  box-shadow: 0 4px 20px rgba(0, 255, 0, 0.2);
  min-width: 400px;
  min-height: 300px;
  z-index: ${props => props.zIndex};
  backdrop-filter: blur(10px);
  display: ${props => props.isMinimized ? 'none' : 'block'};
  
  ${props => props.isMaximized && `
    top: 40px !important;
    left: 0 !important;
    width: 100vw !important;
    height: calc(100vh - 40px) !important;
    border-radius: 0;
  `}

  @media (max-width: 768px) {
    min-width: 300px;
    min-height: 250px;
  }
`;

const WindowHeader = styled.div`
  background: rgba(0, 0, 0, 0.8);
  color: #00ff00;
  padding: 8px 16px;
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move;
  border-bottom: 1px solid rgba(0, 255, 0, 0.3);
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
`;

const WindowContent = styled.div`
  padding: 0;
  height: calc(100% - 40px);
  overflow: auto;
  background: rgba(10, 10, 10, 0.9);
  border-radius: 0 0 8px 8px;
`;

interface WindowProps {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  position: { x: number; y: number };
  size: { width: number; height: number };
  isMaximized: boolean;
  isMinimized: boolean;
  zIndex: number;
}

const Window: React.FC<WindowProps> = ({ 
  id,
  title, 
  component: Component, 
  position, 
  size,
  isMaximized,
  isMinimized,
  zIndex
}) => {
  const { closeWindow, minimizeWindow, maximizeWindow, focusWindow } = useWindows();
  const [windowPosition, setWindowPosition] = useState(position);

  const handleDrag = (e: any, data: any) => {
    setWindowPosition({ x: data.x, y: data.y });
  };

  return (
    <Draggable
      disabled={isMaximized}
      position={isMaximized ? { x: 0, y: 0 } : windowPosition}
      onDrag={handleDrag}
      handle=".window-header"
    >
      <WindowContainer 
        isMaximized={isMaximized}
        isMinimized={isMinimized}
        zIndex={zIndex}
        style={isMaximized ? {} : { width: size.width, height: size.height }}
        onClick={() => focusWindow(id)}
      >
        <WindowHeader className="window-header">
          <span>{title}</span>
          <WindowControls 
            onClose={() => closeWindow(id)}
            onMinimize={() => minimizeWindow(id)}
            onMaximize={() => maximizeWindow(id)}
          />
        </WindowHeader>
        <WindowContent>
          <Component />
        </WindowContent>
      </WindowContainer>
    </Draggable>
  );
};

export default Window;
